import { CollectionConfig } from "@rebasepro/types";
import jobsCollection from "./jobs";
import talentApplicationsCollection from "./talent_applications";

/**
 * Email Threads — one conversation with one set of people.
 *
 * The unit an operator actually works in. "Have we heard back from Acciona" is a
 * question about a conversation, not about a message, and the link to the rest
 * of this database belongs here for the same reason: what a conversation is
 * about is a property of the conversation, not of any one message in it.
 *
 * ── The link ─────────────────────────────────────────────────────────
 *
 * A thread points at *at most one* of four things — a candidate, a company with
 * an account, a scraped employer, or a single candidature — and `none` is a real
 * and common state, because a mailbox receives from strangers.
 *
 * `entity_id` carries no relation and cannot: it names a row in `talents`,
 * `companies` or `talent_applications` depending on `entity_type`. `employer_key`
 * is the prospect side, keyed the way `company_prospects` is keyed and for the
 * reason that file gives at length.
 *
 * `linked_by` is what keeps the guess and the correction from fighting. The
 * poller sets `auto` from the sender's address; a person setting it in the panel
 * writes `manual`, and ingest never touches a `manual` thread again. Without it,
 * a thread an operator moved onto the right company moves back on the next poll.
 *
 * ── Before the link: is it a conversation at all ─────────────────────
 *
 * `kind` (drizzle/0051) says whether there is anybody on the other end. A
 * quarter of the mailbox is machines — the tax office, Google invoicing itself,
 * delivery failures — and those threads are kept, readable, and excluded from
 * every queue: nothing is owed to them and nobody can be assigned to them.
 * Decided at ingest by backend/src/services/email-kind.ts; a person can flip
 * it, and flipping it writes a rule (`email_sender_rules`) for next time.
 *
 * `job_id` is the vacancy the subject line names, read at ingest by
 * backend/src/services/email-vacancy.ts. It is a guess about what the thread
 * is *about*, kept apart from the link, which says who it is *with*; once an
 * operator registers the candidature, `application_id` records that.
 *
 * ── Access ───────────────────────────────────────────────────────────
 *
 * Admin-only, and FORCE RLS in the database (drizzle/0042): every row is private
 * correspondence with a named person. Server paths must go through
 * `rebase.dataAsAdmin` — the owner connection sees this table EMPTY, with no
 * error.
 */
const emailThreadsCollection: CollectionConfig = {
    name: "Conversaciones",
    singularName: "Conversación",
    slug: "email-threads",
    table: "email_threads",
    securityRules: [
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        subject: {
            name: "Asunto",
            type: "string"
        },
        subject_key: {
            name: "Asunto normalizado",
            type: "string",
            description: "El asunto sin 'Re:', 'RV:' ni acentos. Se usa para enlazar una respuesta que no trae cabecera References. Derivado: no editar.",
            admin: { readOnly: true }
        },
        participants: {
            name: "Participantes",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Dirección" },
            description: "Todas las direcciones que han aparecido en la conversación",
            admin: { readOnly: true }
        },
        kind: {
            name: "Tipo",
            type: "string",
            enum: [
                { id: "person", label: "Conversación", color: "green" },
                { id: "automated", label: "Lo escribió una máquina", color: "gray" },
                { id: "broadcast", label: "Envío masivo", color: "gray" }
            ],
            defaultValue: "person",
            description: "Sólo una conversación con una persona puede deber una respuesta o conectarse con un candidato o una empresa. El correo de máquinas se guarda y no se cuenta."
        },
        entity_type: {
            name: "De qué trata",
            type: "string",
            enum: [
                // "Sin asignar" first: it is the default and the honest one. A
                // mailbox receives from people nobody has ever heard of.
                { id: "none", label: "Todavía no se sabe", color: "gray" },
                { id: "talent", label: "Candidato", color: "green" },
                { id: "company", label: "Empresa", color: "blue" },
                { id: "prospect", label: "Empresa que contrata", color: "cyan" },
                { id: "application", label: "Candidatura", color: "purple" }
            ],
            defaultValue: "none"
        },
        entity_id: {
            name: "ID de la ficha",
            type: "string",
            description: "Apunta a talents, companies o talent_applications según el tipo. No es una relación porque son tres tablas distintas.",
            admin: { readOnly: true }
        },
        employer_key: {
            name: "Clave de empresa",
            type: "string",
            description: "La misma identidad normalizada que usan las empresas que contratan (config/employer_identity.ts)",
            admin: { readOnly: true }
        },
        contact_id: {
            name: "Persona de contacto",
            type: "string",
            description: "La persona de prospect_contacts con quien es la conversación, si se reconoció"
        },
        job_id: {
            name: "Vacante",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => jobsCollection,
                localKey: "job_id"
            },
            description: "La vacante que nombra el asunto. Se reconoce al recibir el correo; es una suposición hasta que se registra la candidatura."
        },
        application_id: {
            name: "Candidatura",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => talentApplicationsCollection,
                localKey: "application_id"
            },
            description: "La candidatura registrada desde esta conversación, si alguien la registró.",
            admin: { readOnly: true }
        },
        linked_by: {
            name: "Quién la conectó",
            type: "string",
            enum: [
                { id: "auto", label: "La conectó la sincronización", color: "gray" },
                { id: "manual", label: "A mano", color: "green" }
            ],
            defaultValue: "auto",
            description: "'A mano' gana siempre: la sincronización no vuelve a tocar una conversación que alguien asignó."
        },
        owner: {
            name: "Asignada a",
            type: "string",
            description: "El email de la persona del equipo que va a contestar. Vacío = cualquiera. Ver drizzle/0054."
        },
        follow_up_at: {
            name: "Volver a mirar",
            type: "date",
            description: "Cuándo retomar esta conversación. Se borra sola cuando contestan."
        },
        needs_reply: {
            name: "Falta contestar",
            type: "boolean",
            defaultValue: true,
            description: "Si su último mensaje pide una respuesta. Un «muchas gracias» no la pide: ver services/email-reply.ts. Ver drizzle/0055."
        },
        counterpart_name: {
            name: "Persona",
            type: "string",
            admin: { readOnly: true },
            description: "El nombre de quien está al otro lado, tal y como lo manda su cliente de correo. Ver drizzle/0056."
        },
        last_snippet: {
            name: "Vista previa",
            type: "string",
            admin: { readOnly: true },
            description: "Las primeras palabras del último mensaje, para la lista. La escribe la sincronización."
        },
        status: {
            name: "Estado",
            type: "string",
            enum: [
                { id: "open", label: "Abierta", color: "blue" },
                { id: "closed", label: "Cerrada", color: "gray" }
            ],
            defaultValue: "open"
        },
        unread: {
            name: "Sin leer",
            type: "boolean",
            defaultValue: false
        },
        message_count: {
            name: "Mensajes",
            type: "number",
            defaultValue: 0,
            admin: { readOnly: true }
        },
        last_message_at: {
            name: "Último mensaje",
            type: "date",
            admin: { readOnly: true }
        },
        last_direction: {
            name: "Dirección del último",
            type: "string",
            enum: [
                { id: "inbound", label: "Recibido", color: "green" },
                { id: "outbound", label: "Enviado", color: "blue" }
            ],
            admin: { readOnly: true }
        },
        last_inbound_at: {
            name: "Última respuesta suya",
            type: "date",
            description: "Aparte del anterior a propósito: 'cuándo contestaron' es la fecha que decide si debemos un seguimiento.",
            admin: { readOnly: true }
        },
        last_outbound_at: {
            name: "Último envío nuestro",
            type: "date",
            admin: { readOnly: true }
        },
        gmail_thread_id: {
            name: "Conversación en Gmail",
            type: "string",
            description: "X-GM-THRID. Es lo que hace que el panel y Gmail agrupen igual, y lo que permite 'Abrir en Gmail'.",
            admin: { readOnly: true }
        },
        labels_signature: {
            name: "Etiquetas escritas",
            type: "string",
            description: "Las etiquetas SustenTalent/ con las que se escribió por última vez. Igual a las deseadas = nada que escribir.",
            admin: { readOnly: true }
        },
        labels_synced_at: {
            name: "Etiquetas sincronizadas",
            type: "date",
            admin: { readOnly: true }
        },
        created_at: {
            name: "Creada",
            type: "date",
            autoValue: "on_create"
        },
        updated_at: {
            name: "Actualizada",
            type: "date",
            autoValue: "on_update"
        }
    },
    admin: {
        group: "Correo",
        icon: "Mail",
        // Not in `navigationGroupMappings`, like `company_prospects`: "Correo" is
        // the screen these rows are meant to be used from, and a raw table view
        // of them would be a second, much worse inbox.
        sort: ["last_message_at", "desc"],
        // Fuera del menú: esta tabla es el interior de "Correo",
        // no una pantalla. Como entrada del menú era tres cosas confusas a la
        // vez — vacía hasta que alguien actúa, sin explicar qué es, y un
        // segundo camino peor hacia "Correo".
        //
        // `group` por sí solo NO la ocultaba: el menú muestra cualquier
        // colección que declare uno, esté o no en `navigationGroupMappings`.
        // Sigue siendo navegable — el lápiz de la pantalla abre la fila.
        hideFromNavigation: true
    }
};

export default emailThreadsCollection;
